import React, { useEffect, useState } from 'react';
import { FormatToShortPriceTemplate, formatToShortString } from '../../utils/FormatValue';
import { ArcElement, Chart as ChartJS, Legend, Tooltip, TooltipItem } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { MonthYear } from '../../utils/MonthYear/monthYear';

ChartJS.register(ArcElement, Tooltip, Legend);

export interface IDashboardDonutChartDataSet {
  fieldName: string;
  color: string;
  value: number;
  origVal?: number;
  quantity?: number;
  unit?: string;
  price?: number;
  ratio: number;
  valuesArray?: { date: MonthYear; value: number }[];
}

export interface DashboardDonutChartProps {
  dataSet: IDashboardDonutChartDataSet[];
  secondaryTitle: string;
  ratioLine?: string;
  cssSize?: string;
  isQuantity?: boolean;
  quantityUnitLabel?: string;
}

const createCenterTextPlugin = (
  dataSet: IDashboardDonutChartDataSet[],
  secondaryTitle: string,
  ratioLine: string,
  isQuantity: boolean,
  quantityUnitLabel?: string,
) => ({
  id: 'customCenterText',
  beforeDraw(chart: ChartJS) {
    const width = chart.width as number;
    const height = chart.height as number;
    const ctx = chart.ctx as CanvasRenderingContext2D;
    ctx.restore();
    const fontSize = (height / 175).toFixed(2);
    ctx.textBaseline = 'middle';

    // Add the sum of the data array - ensure numeric addition
    // Exclude "Other formulas" placeholder from the center total (it's just for proportion display)
    const sum = dataSet
      .filter(({ fieldName }) => !!fieldName && fieldName !== 'Other formulas')
      .reduce((acc, item) => {
        const numAcc = typeof acc === 'number' ? acc : parseFloat(String(acc)) || 0;
        const numVal = typeof item.value === 'number' ? item.value : parseFloat(String(item.value)) || 0;
        return numAcc + numVal;
      }, 0 as number);

    // Format center value:
    // - Monetary mode: price formatting with $ and K/M
    // - Quantity mode: quantity with unit label (e.g. "1,234 MT")
    const sumText = isQuantity
      ? `${formatToShortString(sum, 0)} ${quantityUnitLabel || 'MT'}`
      : FormatToShortPriceTemplate(sum);
    const sumFontSize = parseFloat(fontSize) * 1.5; // Double the font size for sum text
    ctx.font = `600 ${sumFontSize}em Inter, sans-serif`; // Use theme font (Inter) with semi-bold weight

    // Calculate sum text position
    const sumTextX = Math.round((width - ctx.measureText(sumText).width) / 2);
    const sumTextY = height / 2.25 - sumFontSize / 2; // Center vertically

    // Draw the sumText
    ctx.fillStyle = '#000000'; // Black color
    ctx.fillText(sumText, sumTextX, sumTextY);

    // Secondary row shows the alternate mode total (value mode → qty, qty mode → value)
    const subtitle = secondaryTitle;

    if (subtitle && subtitle.trim()) {
      ctx.font = `500 ${fontSize}em Inter, sans-serif`; // Use theme font for secondary text with medium weight
      ctx.fillStyle = '#9F9F9F'; // Default text color

      const mainTextX = Math.round((width - ctx.measureText(subtitle).width) / 2);
      const mainTextY = height / 1.75 + sumFontSize / 2; // Position below sum text
      ctx.fillText(subtitle, mainTextX, mainTextY);
    }
    if (ratioLine) {
      ctx.font = `${sumFontSize * 0.6}em sans-serif`;
      ctx.fillStyle = '#000000'; // Black color
      const rTextX = Math.round((width - ctx.measureText(ratioLine).width) / 2);
      const rTextY = height / 1.75 + sumFontSize * 20; // Position below sum text
      ctx.fillText(ratioLine, rTextX, rTextY);
    }
    ctx.save();
  },
});

const grayBackgroundOnEmptyData = {
  id: 'grayBackgroundOnEmptyData',
  afterDraw(chart: any) {
    if (chart.config.type !== 'doughnut') return; // Apply only to Doughnut charts

    const ctx = chart.ctx;
    const dataSum = chart.data.datasets[0].data.reduce((a: any, b: any) => a + b, 0);

    if (dataSum === 0) {
      // If all data is 0, adjust to draw a gray circle border without cutting the edges
      const { chartArea } = chart;
      const centerX = (chartArea.left + chartArea.right) / 2;
      const centerY = (chartArea.top + chartArea.bottom) / 2;
      const outerRadius = (chartArea.right - chartArea.left) / 2; // Full radius of the chart
      const innerRadius = outerRadius * 0.8; // Use the cutout percentage

      ctx.save();
      ctx.beginPath();
      ctx.arc(centerX, centerY, outerRadius - 1, 0, 2 * Math.PI); // Outer circle
      ctx.arc(centerX, centerY, innerRadius + 1, 0, 2 * Math.PI, true); // Inner cutout
      ctx.fillStyle = '#EAEAEB'; // Set the fill color to gray
      ctx.fill();
      ctx.restore();
    }
  },
};
export const DashBoardDonutCard: React.FC<DashboardDonutChartProps> = ({
  secondaryTitle,
  dataSet,
  ratioLine,
  cssSize = 'w-36 h-36',
  isQuantity = false,
  quantityUnitLabel,
}) => {
  const [key, setKey] = useState(0);
  useEffect(() => {
    setKey((prevKey) => prevKey + 1);
  }, [dataSet]);

  const options = {
    animation: false,
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      tooltip: {
        backgroundColor: 'rgb(0, 0, 156)',
        filter: ({ label }: TooltipItem<any>) => !!label,
      },
      legend: {
        display: false,
      },
    },
  };
  
  const chartData = {
    labels: dataSet.map((item) => item.fieldName),
    datasets: [
      {
        data: dataSet.map((item) => item.origVal || item.value),
        backgroundColor: dataSet.map((item) => item.color),
        borderWidth: 2,
        radius: '95%',
        cutout: '80%',
      },
    ],
  };
  
  return (
    <div className={cssSize}>
      <Doughnut
        key={key}
        data={chartData}
        options={options}
        plugins={[
          createCenterTextPlugin(dataSet, secondaryTitle, ratioLine || '', isQuantity, quantityUnitLabel),
          grayBackgroundOnEmptyData,
        ]}
      />
    </div>
  );
};
